import React from "react";

const PrivacyPolicy = () => {
  return (
    <div className="container mt-5">
      <div className="card p-4">
        <h2 className="card-title mb-4">StudMart Privacy Policy</h2>

        <p className="mb-3">
          Welcome to StudMart! We are committed to protecting your privacy and
          ensuring the security of your personal information. This Privacy
          Policy outlines how we collect, use, and safeguard your information
          when you use our website and platform.
        </p>

        <h5 className="mb-3">1. Information We Collect</h5>
        <p className="mb-3">
          To provide you with our services, we collect the following types of
          information:
        </p>
        <ul>
          <li>
            <strong>Account Information:</strong> When you register for
            StudMart, we collect your name, college email address, student ID
            (for verification purposes), and enrollment information.
          </li>
          <li>
            <strong>Profile Information:</strong> You may choose to provide
            additional information for your public profile, such as your major,
            interests, and profile picture.
          </li>
          <li>
            <strong>Transaction Information:</strong> When you engage in
            transactions on StudMart, we may collect information related to
            those transactions, such as item details, prices, and communication
            between users.
          </li>
          <li>
            <strong>Usage Information:</strong> We collect information about how
            you use our platform, including the pages you visit, the listings
            you view, and your interactions with other users.
          </li>
          <li>
            <strong>Device and Technical Information:</strong> We may collect
            information about your device, browser type, IP address, and
            operating system to help us improve our platform and ensure its
            security.
          </li>
          <li>
            <strong>Communications:</strong> We may retain records of your
            communications with us and other users through our in-app messaging
            system.
          </li>
        </ul>

        <h5 className="mb-3">2. How We Use Your Information</h5>
        <p className="mb-3">
          We use the collected information for various purposes, including:
        </p>
        <ul>
          <li>
            <strong>Account Management:</strong> To create and manage your
            StudMart account, verify your student status, and provide you with
            access to our platform.
          </li>
          <li>
            <strong>Facilitating Transactions:</strong> To connect buyers and
            sellers, facilitate communication, and ensure the smooth operation
            of the marketplace.
          </li>
          <li>
            <strong>Personalization:</strong> To personalize your experience on
            StudMart, such as showing you relevant listings and connecting you
            with students with shared academic interests.
          </li>
          <li>
            <strong>Communication:</strong> To communicate with you about your
            account, transactions, platform updates, and important
            announcements.
          </li>
          <li>
            <strong>Improving Our Services:</strong> To analyze user behavior,
            identify areas for improvement, and develop new features for
            StudMart.
          </li>
          <li>
            <strong>Security and Fraud Prevention:</strong> To monitor and
            prevent fraudulent activities, ensure the safety and security of our
            platform, and enforce our terms of service.
          </li>
          <li>
            <strong>Customer Support:</strong> To provide you with assistance
            and respond to your inquiries.
          </li>
        </ul>

        <h5 className="mb-3">3. How We Share Your Information</h5>
        <p className="mb-3">
          We are committed to protecting your privacy and will only share your
          information in the following limited circumstances:
        </p>
        <ul>
          <li>
            <strong>With Other Users:</strong> When you engage in a transaction,
            certain information, such as your public profile and the details of
            the item, will be visible to the other user involved.
          </li>
          <li>
            <strong>Service Providers:</strong> We may share your information
            with trusted third-party service providers who assist us in
            operating our platform, such as payment processors, verification
            services, and analytics providers. These providers are contractually
            obligated to protect your information.
          </li>
          <li>
            <strong>Legal Compliance:</strong> We may disclose your information
            if required by law, legal process, or governmental request.
          </li>
          <li>
            <strong>Business Transfers:</strong> In the event of a merger,
            acquisition, or sale of all or a portion of our assets, your
            information may be transferred to the acquiring entity. We will
            notify you if such a transfer occurs and your information becomes
            subject to a different privacy policy.
          </li>
          <li>
            <strong>With Your Consent:</strong> We may share your information
            with third parties if you have given us your explicit consent to do
            so.
          </li>
        </ul>
        <p className="mb-3">
          We will <strong>never</strong> sell your personal information to third
          parties for marketing purposes.
        </p>

        <h5 className="mb-3">4. Data Security</h5>
        <p className="mb-3">
          We take reasonable measures to protect your personal information from
          unauthorized access, use, or disclosure. These measures include:
        </p>
        <ul>
          <li>Secure server infrastructure</li>
          <li>Encryption of sensitive data</li>
          <li>Regular security assessments and updates</li>
          <li>Employee training on data privacy and security practices</li>
        </ul>
        <p className="mb-3">
          While we strive to protect your information, no method of transmission
          over the internet or electronic storage is completely secure.
          Therefore, we cannot guarantee absolute security.
        </p>

        <h5 className="mb-3">5. Your Rights</h5>
        <p className="mb-3">
          You have certain rights regarding your personal information,
          including:
        </p>
        <ul>
          <li>
            <strong>Access:</strong> You have the right to request access to the
            personal information we hold about you.
          </li>
          <li>
            <strong>Correction:</strong> You have the right to request that we
            correct any inaccurate or incomplete personal information.
          </li>
          <li>
            <strong>Deletion:</strong> You have the right to request the
            deletion of your personal information, subject to certain
            exceptions.
          </li>
          <li>
            <strong>Objection to Processing:</strong> You may have the right to
            object to the processing of your personal information in certain
            circumstances.
          </li>
          <li>
            <strong>Data Portability:</strong> You may have the right to receive
            your personal information in a structured, commonly used, and
            machine-readable format.
          </li>
        </ul>
        <p className="mb-3">
          To exercise these rights, please contact us using the information
          provided below. We may require you to verify your identity before
          processing your request.
        </p>

        <h5 className="mb-3">6. Data Retention</h5>
        <p className="mb-3">
          We will retain your personal information for as long as necessary to
          provide you with our services, comply with legal obligations, resolve
          disputes, and enforce our agreements. When your account is terminated,
          we may retain certain information for a reasonable period for these
          purposes.
        </p>

        <h5 className="mb-3">7. Children's Privacy</h5>
        <p className="mb-3">
          StudMart is intended for use by college students. We do not knowingly
          collect personal information from individuals under the age of 13. If
          you are a parent or guardian and believe that your child has provided
          us with personal information, please contact us immediately, and we
          will take steps to delete such information.
        </p>

        <h5 className="mb-3">8. Changes to This Privacy Policy</h5>
        <p className="mb-3">
          We may update this Privacy Policy from time to time. We will notify
          you of any significant changes by posting the updated policy on our
          website or through other communication channels. Your continued use of
          StudMart after the effective date of the revised Privacy Policy
          constitutes your acceptance of the changes.
        </p>

        <h5 className="mb-3">9. Contact Us</h5>
        <p className="mb-3">
          If you have any questions or concerns about this Privacy Policy or our
          data practices, please contact us at:
        </p>
        <p className="mb-0">Email: dixitshivam249@gmail.com</p>
        {/* You can add a physical address if applicable */}
      </div>
    </div>
  );
};

export default PrivacyPolicy;
